/**
 * MyNft Component - Expert Refactor with Reusable Utilities
 * 
 * Improvements:
 * - Memoized components and callbacks for performance
 * - Custom hooks for better state management (useModalState, useNFTOperations)
 * - Reusable utilities (errorUtils, textUtils, sliderSettings)
 * - Enhanced accessibility and error handling
 * - Cleaner separation of concerns
 * - Optimized rendering logic with proper memoization
 */

import React, { memo, useCallback, useMemo, useRef, useEffect } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { Swiper } from "swiper/react";
import { Navigation } from "swiper";
import { NavLink } from "react-router-dom";

// Components
import Loader from "@common/Loader";
import Slider from "react-slick";
import Card from "@common/Card";
import Modal from "@modals/Modal";
import Popup from "@modals/Popup";
import SalePopup from "@modals/SalePopup";
import PiePopup from "@modals/PiePopup";
import ShareModal from "@modals/ShareModal";
import Spinner from "@common/Spinner";

// Hooks and Context
import { useAppContext } from "@context/AppContext";
import { useModalState } from "@hooks/useModalState";
import { useNFTOperations } from "@hooks/useNFTOperations";
import { useSliderSettings } from "@hooks/useSliderSettings";

// Utils
import { logInfo } from "@utils/log";
import { useApiClient } from "@hooks/useApiClient";

// Assets
import vzla2 from "@assets/images/vzla2.svg";

// Constants
const SWIPER_BREAKPOINT = 3;
const SWIPER_CONFIG = {
  slidesPerView: 4,
  spaceBetween: 50,
  navigation: true,
  modules: [Navigation],
  className: "mySwiper"
};

// Memoized Download Handler with AbortController
const useDownloadHandler = (baseURL) => {
  const abortControllerRef = useRef();

  useEffect(() => {
    return () => {
      // Cleanup any pending requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return useCallback((card) => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    logInfo('Downloading image:', card);

    fetch(`${baseURL}/download`, {
      method: "POST",
      headers: { "Content-type": "application/json" },
      body: JSON.stringify({ url: card.image }),
      signal: abortControllerRef.current.signal,
    })
    .then(res => {
      if (res.ok) {
        return res.blob();
      }
      throw new Error(`HTTP error! status: ${res.status}`);
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${card.name}.png`;
      link.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(err => {
      if (err.name !== 'AbortError') {
        logInfo('Download error:', err);
      }
    });
  }, [baseURL]);
};

// Simple No NFT Message Component (no memo - simple JSX, props rarely change)
const NoNFTMessage = ({ nftName, hasViewMore = false }) => (
  <h1 className="no-nft-text" role="status" aria-live="polite">
    <NavLink 
      to="/mint" 
      className={hasViewMore ? "link-design" : undefined}
      aria-label={`Mint your first ${nftName}`}
    >
      Buy
    </NavLink>
    {" your first "}{nftName}!
  </h1>
);

// Memoized NFT Grid Component (good candidate - complex rendering logic)
const NFTGrid = memo(({ 
  nftData, 
  newest, 
  onSaleAction, 
  onPieOpen, 
  onShare 
}) => (
  <div className="my-nfts" style={{ justifyContent: "center" }}>
    {nftData.map((card) => (
      <div className="nft-grid-simplecard__wrapper" key={card.id}>
        <Card
          card={card}
          btn={card.forSale ? "Cancel Sale" : "Sell"}
          download={true}
          overlay={false}
          overlayText={card.forSale}
          newest={newest}
          onBtnOpen={(card, btn) => onSaleAction(card, btn)}
          onPieOpen={() => onPieOpen(card)}
          onShare={() => onShare(card)}
        />
      </div>
    ))}
  </div>
));
NFTGrid.displayName = 'NFTGrid';

// Memoized Swiper Component (good candidate - complex rendering with many cards)
const NFTSwiper = memo(({ 
  nftData, 
  newest, 
  onSaleAction, 
  onPieOpen, 
  onShare 
}) => (
  <div className="mynft__swipper">
    <Swiper {...SWIPER_CONFIG}>
      <div className="swiper-wrapper swiper-wrap">
        {nftData.map((card) => (
          <div key={card.id} className="swiper-slide">
            <Card
              card={card}
              btn={card.forSale ? "Cancel Sale" : "Sell"}
              download={true}
              overlay={false}
              overlayText={card.forSale}
              newest={newest}
              onBtnOpen={(card, btn) => onSaleAction(card, btn)}
              onPieOpen={() => onPieOpen(card)}
              onShare={() => onShare(card)}
            />
          </div>
        ))}
      </div>
    </Swiper>
  </div>
));
NFTSwiper.displayName = 'NFTSwiper';

// Memoized Mobile Slider Component (good candidate - complex rendering)
const NFTMobileSlider = memo(({ 
  nftData, 
  newest, 
  sliderSettings,
  onSaleAction, 
  onPieOpen, 
  onShare 
}) => (
  <Slider {...sliderSettings}>
    {nftData.map((card) => (
      <Card
        key={card.id}
        card={card}
        btn={card.forSale ? "Cancel Sale" : "Sell"}
        download={true}
        overlay={false}
        overlayText={card.forSale}
        newest={newest}
        onBtnOpen={(card, btn) => onSaleAction(card, btn)}
        onPieOpen={() => onPieOpen(card)}
        onShare={() => onShare(card)}
      />
    ))}
  </Slider>
));
NFTMobileSlider.displayName = 'NFTMobileSlider';

const MyNft = memo(() => {
  const {
    themeConfig,
    siteDetails,
    walletAddress,
    connectWallet,
    isWalletConnected,
    nftData,
    newest,
    loading,
    refreshNftData,
    setError,
  } = useAppContext();
  const { baseURL } = useApiClient();

  // Custom hooks
  const {
    modals,
    modalData,
    openSaleModal,
    closeSaleModal,
    openShareModal,
    closeShareModal,
    openPieModal,
    closePieModal,
    updateModalData,
  } = useModalState();

  const { spinner, cancelSale, placeSale } = useNFTOperations(
    { 
      siteDetails, 
      themeConfig, 
      isWalletConnected, 
      connectWallet, 
      refreshNftData 
    },
    { setError, closeSaleModal }
  );

  const sliderSettings = useSliderSettings({
    infinite: false,
    speed: 300,
  });

  const downloadImage = useDownloadHandler(baseURL);

  // Memoized computed values
  const nftName = useMemo(() => 
    themeConfig?.APP_NFT_NAME || 'NFT', 
    [themeConfig?.APP_NFT_NAME]
  );

  const nftPlural = useMemo(() => 
    themeConfig?.APP_NFT_PLURAL || 'NFTs', 
    [themeConfig?.APP_NFT_PLURAL]
  );

  const hasNFTData = useMemo(() => 
    Array.isArray(nftData) && nftData.length > 0, 
    [nftData]
  );

  const shouldShowSwiper = useMemo(() => 
    hasNFTData && nftData.length > SWIPER_BREAKPOINT, 
    [hasNFTData, nftData?.length]
  );

  // Handlers
  const handleSaleAction = useCallback((card, action) => {
    openSaleModal(card, action);
  }, [openSaleModal]);

  const handleCancelSale = useCallback(() => {
    cancelSale(modalData.card);
  }, [cancelSale, modalData.card]);

  const handlePlaceSale = useCallback(() => {
    placeSale(modalData.card, modalData.priceDist);
  }, [placeSale, modalData.card, modalData.priceDist]);

  const handlePriceChange = useCallback((value) => {
    updateModalData({ priceDist: value });
  }, [updateModalData]);

  // Render functions with memoization
  const renderWalletPrompt = useCallback(() => (
    <NoNFTMessage nftName={nftName} hasViewMore />
  ), [nftName]);

  const renderEmptyState = useCallback(() => (
    <NoNFTMessage nftName={nftName} />
  ), [nftName]);

  const renderLoadingState = useCallback(() => (
    <div className="nft-cards">
      <Loader loading={loading} />
    </div>
  ), [loading]);

  const renderDesktopNFTs = useCallback(() => {
    if (!walletAddress) return renderWalletPrompt();
    if (loading) return renderLoadingState();
    if (!hasNFTData) return renderEmptyState();

    return shouldShowSwiper ? (
      <NFTSwiper
        nftData={nftData}
        newest={newest}
        onSaleAction={handleSaleAction}
        onPieOpen={openPieModal}
        onShare={openShareModal}
      />
    ) : (
      <NFTGrid
        nftData={nftData}
        newest={newest}
        onSaleAction={handleSaleAction}
        onPieOpen={openPieModal}
        onShare={openShareModal}
      />
    );
  }, [
    walletAddress,
    loading,
    hasNFTData,
    shouldShowSwiper,
    nftData,
    newest,
    handleSaleAction,
    openPieModal,
    openShareModal,
    renderWalletPrompt,
    renderLoadingState,
    renderEmptyState
  ]);

  const renderMobileNFTs = useCallback(() => {
    if (!walletAddress) return renderWalletPrompt();
    if (loading) return renderLoadingState();
    if (!hasNFTData) return renderEmptyState();

    return (
      <NFTMobileSlider
        nftData={nftData}
        newest={newest}
        sliderSettings={sliderSettings}
        onSaleAction={handleSaleAction}
        onPieOpen={openPieModal}
        onShare={openShareModal}
      />
    );
  }, [
    walletAddress,
    loading,
    hasNFTData,
    nftData,
    newest,
    sliderSettings,
    handleSaleAction,
    openPieModal,
    openShareModal,
    renderWalletPrompt,
    renderLoadingState,
    renderEmptyState
  ]);

  // Modal render functions
  const renderSaleModal = () => {
    if (!modalData.card || Object.keys(modalData.card).length === 0) return null;
    
    return (
      <Modal show={modals.sale}>
        <Popup>
          <SalePopup
            sliders={siteDetails.sliders}
            card={modalData.card}
            show={modals.sale}
            saleStatus={modalData.saleStatus}
            onClose={closeSaleModal}
            onCancel={handleCancelSale}
            onSale={handlePlaceSale}
            onChangePrice={handlePriceChange}
          />
        </Popup>
      </Modal>
    );
  };

  const renderPieModal = () => (
    <Modal show={modals.pie}>
      <Popup>
        <PiePopup 
          card={modalData.popUpCard} 
          sliders={siteDetails.sliders} 
          onClose={closePieModal} 
        />
      </Popup>
    </Modal>
  );

  const renderShareModal = () => {
    if (!modalData.card || Object.keys(modalData.card).length === 0) return null;
    
    return (
      <Modal show={modals.share}>
        <Popup>
          <ShareModal
            card={modalData.card}
            onDownload={downloadImage}
            onClose={closeShareModal}
          />
        </Popup>
      </Modal>
    );
  };

  return (
    <>
      <span className="link_wrapper" id="my"></span>
      <section
        className="default-settings"
        aria-labelledby="mynft-title"
        style={{
          backgroundImage: `url(${vzla2})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="mynft__title-box">
          <div className="mynft__title-column column1"></div>
          <div className="mynft__title-column column2">
            <h1 id="mynft-title" className="mynft__title">
              My {nftPlural}
            </h1>
          </div>
          <div className="mynft__title-column column3">
            {hasNFTData && (
              <NavLink to="/my" aria-label={`View all ${nftPlural}`}>
                <h3 className="mynft__viewall">View More</h3>
              </NavLink>
            )}
          </div>
        </div>
        
        <div className="mynft-cards">{renderDesktopNFTs()}</div>
        <div className="mynft-cards-mobile">{renderMobileNFTs()}</div>
        
        <Spinner spinner={spinner} />
        
        {/* Modals */}
        {renderSaleModal()}
        {renderPieModal()}
        {renderShareModal()}
      </section>
    </>
  );
});

MyNft.displayName = 'MyNft';

export default MyNft;
